import React from 'react'
import { Loading, useGetBusiness } from '@marketing-milk/frontend'
import {
  Campaign,
  CampaignPauseActionType,
  PublishCampaignDTO,
  SavedAudience,
} from '@marketing-milk/interfaces'
import { Box, Drawer, Tooltip } from '@mui/material'
import { AdObjectType, campaignService } from 'app/services'
import { getApiErrorMessage } from 'app/util'
import axios from 'axios'
import { DataTable, DataTableProps } from 'components/DataTable'
import { ColumnSchema, TableSchema } from 'components/DataTable/types'
import { NoItemsCreated } from 'components/NoItemsCreated/NoItemsCreated'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { campaignsTableSchema, endDateColumn, startDateColumn } from '../../campaignsTableSchema'
import { CampaignForm } from '../../forms/CampaignForm'
import { getAdErrors, getAdsetErrors } from '../../helpers/campaign.helpers'
import { singleCampaignDtoIsValid } from '../../helpers/campaignForm.helpers'
import { CampaignTools } from '../campaign-tools/CampaignTools'
import { PauseAdObject } from '../PauseAdObject'
import MMLogoSrc from 'public/img/logos/milk-gradient.png'
import FBLogoSrc from 'public/img/logos/facebook-logo.jpg'
import { CampaignExpandDetails } from './CampaignExpandDetails'
import DialogModal from 'components/material/dialog/Dialog'
import { SettingsSummary } from '../../../../../components'

export type ActionType = 'create' | 'retry' | 'duplicate' | 'extend' | 'edit' | 'delete'

type Props = {
  campaigns?: Campaign[]
  audiences?: SavedAudience[]
  refetch: () => void
  onDeleteCampaign?: (campaignId: number) => void
}
export const CampaignsContent = ({ campaigns, refetch, audiences, onDeleteCampaign }: Props) => {
  const business = useGetBusiness()
  const [editType, setEditType] = useState<ActionType>()
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>()
  const [uploading, setUploading] = useState(false)
  const [setupCampaign, setSetupCampaign] = useState<Campaign>()

  const actionHandler = (selectedCampaign: Campaign) => (type: ActionType) => {
    if (type === 'delete' && onDeleteCampaign) {
      onDeleteCampaign(selectedCampaign.id)
    } else {
      openDrawer(selectedCampaign, type)
    }
  }

  const openDrawer = (campaign: Campaign, type: ActionType) => {
    setSelectedCampaign(campaign)
    setEditType(type)
  }

  const closeDrawer = () => {
    setSelectedCampaign(undefined)
    setEditType(undefined)
  }

  const onSubmit = async (campaignDto?: PublishCampaignDTO) => {
    if (!campaignDto) return
    if (!selectedCampaign) return
    if (!singleCampaignDtoIsValid(campaignDto, () => {})) return
    setUploading(true)

    closeDrawer()

    try {
      if (editType === 'edit' || editType === 'retry') {
        toast.info('We are adding your edits to the campaign on facebook')
        await campaignService.patchCampaign(business.id, selectedCampaign.id, campaignDto)
      }
      if (editType === 'duplicate') {
        toast.info('We are duplicating your campaign')
        await campaignService.duplicateCampaign(business.id, selectedCampaign.id, campaignDto)
      }

      await refetch()
      toast.success('Success!')
    } catch (err) {
      await refetch()
      toast.error(`Campaign could not be published: ${getApiErrorMessage(err)}`)
    } finally {
      setUploading(false)
    }
  }

  const onPauseOrActivate = async (
    adObjectType: AdObjectType,
    adObjectID: number,
    action: CampaignPauseActionType
  ): Promise<boolean> => {
    try {
      await campaignService.pauseOrResumeAdObjectForBusiness(
        business.id,
        adObjectID,
        adObjectType,
        action
      )
      await refetch()
      toast.success(`Your ${adObjectType} has been successfully ${action}d`)
      return true
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data.message) {
        toast.error(`Your ${adObjectType} failed to be ${action}d: ${err.response.data.message}`)
      } else {
        toast.error(`Your ${adObjectType} failed to be ${action}d`)
      }
      await refetch()
      return false
    }
  }

  const campaignNameCell: ColumnSchema<Campaign> = {
    tooltipText: 'Select a Marketing Milk campaign to view its setup.',
    render: campaign => (
      <div className="flex min-w-[200px] items-start gap-2.5">
        <Tooltip
          title={campaign.config ? 'Built in Marketing Milk' : 'Built in Facebook Ads Manager'}
        >
          <img
            src={campaign.config ? MMLogoSrc : FBLogoSrc}
            alt={campaign.config ? 'Built in Marketing Milk' : 'Built in Facebook Ads Manager'}
            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded object-contain"
          />
        </Tooltip>
        {campaign.config ? (
          <button
            type="button"
            className="text-mm-primary-600 hover:text-mm-primary-700 max-w-xs break-words text-left font-medium hover:underline"
            aria-label={`View the setup for ${campaign.name}`}
            onClick={() => setSetupCampaign(campaign)}
          >
            {campaign.name}
          </button>
        ) : (
          <span className="text-mm-neutral-2-900 max-w-xs break-words font-medium">
            {campaign.name}
          </span>
        )}
      </div>
    ),
  }

  const campaignColumns: TableSchema<Campaign> = {
    'Off / On': {
      tooltipText:
        'This column indicates whether your campaign is currently on or off. When switched on, it will be able to run based on its schedule and settings. When switched off, it will not run.',
      render: campaign => (
        <PauseAdObject
          key={campaign.id}
          checkedDefault={campaign.activeStatus === 'ACTIVE'}
          disabled={!campaign.activeStatus}
          onPauseOrResume={checked =>
            onPauseOrActivate('Campaign', campaign.id, checked ? 'resume' : 'pause')
          }
        />
      ),
    },
    'Campaign Name': campaignNameCell,
    Schedule: {
      tooltipText: 'The start and end dates of the campaign',
      render: campaign => (
        <div className="whitespace-nowrap text-xs">
          <div>
            <span className="text-mm-neutral-2-500 mr-2 inline-block w-8">From</span>
            {startDateColumn.render(campaign)}
          </div>
          <div className="mt-1">
            <span className="text-mm-neutral-2-500 mr-2 inline-block w-8">To</span>
            {endDateColumn.render(campaign)}
          </div>
        </div>
      ),
    },
    ...campaignsTableSchema,
    'Start Date': { ...startDateColumn, hidden: true },
    'End Date': { ...endDateColumn, hidden: true },
    Actions: {
      render: campaign => <CampaignTools campaign={campaign} action={actionHandler(campaign)} />,
    },
  }
  // The shared schema carries its own plain-text name column, so the linked version is put
  // back after the merge — the entry above only holds the column's position.
  campaignColumns['Campaign Name'] = campaignNameCell

  return (
    <>
      <Drawer open={!!selectedCampaign || editType === 'create'} onClose={closeDrawer}>
        <CampaignForm
          // @ts-ignore
          dto={selectedCampaign?.config}
          // `status` only exists once a campaign has published to Facebook, so it — and not
          // `isPublished`, which is true for any existing draft — is what locks delivery.
          // A duplicate creates a new Meta campaign, so its delivery remains editable.
          isLiveOnFacebook={!!selectedCampaign?.status && editType !== 'duplicate'}
          campaignErrors={{
            campaign: selectedCampaign?.errors?.map(s => s.message),
            adsets: getAdsetErrors(selectedCampaign)?.map(s => s.message),
            ads: getAdErrors(selectedCampaign)?.map(s => s.message),
          }}
          audiences={audiences}
          // Duplicating is the only action here that mints a new Meta campaign; edit, retry and
          // extend all patch an existing one, where the flag is deliberately ignored.
          canPublishPaused={editType === 'duplicate'}
          onSubmit={onSubmit}
        />
      </Drawer>

      <DialogModal
        title={setupCampaign ? `${setupCampaign.name} — setup` : 'Campaign setup'}
        maxWidth="lg"
        isOpen={!!setupCampaign}
        onOpen={() => {}}
        onClose={() => setSetupCampaign(undefined)}
        content={
          setupCampaign?.config ? (
            <SettingsSummary
              campaignDto={setupCampaign.config}
              schedule={setupCampaign.adjustmentSchedule}
              rollingCostPer={setupCampaign.rollingCostPer ?? undefined}
            />
          ) : null
        }
      />

      {uploading && <Loading loadingMessage="We are adding your changes to facebook" />}

      {isEmpty(campaigns) && (
        <div className="w-full flex justify-center">
          <NoItemsCreated title="No Campaigns To Show" />
        </div>
      )}

      {campaigns && campaigns.length > 0 && !uploading && (
        <Box
          sx={{
            '& .MuiTableContainer-root': { boxShadow: 'none', borderRadius: '8px', padding: 0 },
            '& .MuiTableCell-root': {
              borderColor: '#e5e7eb',
              padding: '12px',
              fontSize: '13px',
              fontVariantNumeric: 'tabular-nums',
            },
            '& .MuiTableCell-head': {
              backgroundColor: '#f9fafb',
              color: '#6b7280',
              fontSize: '12px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              textTransform: 'none',
              letterSpacing: 'normal',
            },
            '& .MuiTableCell-root > .flex.justify-center': { justifyContent: 'flex-start' },
            '& .MuiCollapse-container .MuiTableCell-root': { padding: '6px 12px' },
            '& .MuiCollapse-container h2, & .MuiCollapse-container h3': {
              margin: '0 0 8px',
              fontSize: '13px',
              lineHeight: '20px',
            },
            '& .MuiTableRow-root.Mui-selected': { backgroundColor: '#f9fafb' },
            '& .MuiTableRow-root.Mui-selected:hover': { backgroundColor: '#f3f4f6' },
          }}
        >
          <p className="text-mm-neutral-2-500 mb-3 px-3 text-xs">
            Expand a campaign to browse its adsets and ads. Select a campaign name to view its
            setup.
          </p>
          <CampaignsTable
            hidePagination
            hideColumnFilter
            hideSearch
            tableSchema={campaignColumns}
            expansionPanel={{
              shouldExpand: () => true,
              render: campaign => (
                <CampaignExpandDetails campaign={campaign} onPauseOrActivate={onPauseOrActivate} />
              ),
            }}
            recordsList={campaigns}
          />
        </Box>
      )}
    </>
  )
}

export const CampaignsTable = ({ recordsList: campaigns, ...props }: DataTableProps<Campaign>) => (
  <DataTable<Campaign> recordsList={campaigns} {...props} />
)
